/**
 * Apnea DTO types and the enum bridge between the API and the rule engine.
 *
 * Kept separate from apneaService.ts so pure logic (utils/apneaProgress.ts and its
 * tests) can import these types without pulling in the HTTP client — services/api.ts
 * imports axios, which Jest cannot transform.
 */

/**
 * Apnea training API client.
 *
 * Enum values match the C# names the backend serialises ("Co2", "Timed"), not the
 * lowercase discriminators used inside the rule engine. `toRuleSessionType` bridges
 * the two so the rule engine keeps its own vocabulary.
 */

export type ApiSessionType =
  | 'Co2' | 'O2' | 'NoContraction' | 'ApneaWalk' | 'DryStatic' | 'Bolt' | 'Breathing';

export type ApiTimeSource = 'Timed' | 'Manual' | 'Corrected';
export type ApiDecision = 'Repeat' | 'Reduce' | 'Progress';
export type ApiWarningKind = 'None' | 'Lmc' | 'Blackout' | 'Other';

export interface ApiPlannedSet {
  set: number;
  holdSeconds: number;
  ventSeconds: number;
}

export interface ApiActualSet {
  set: number;
  holdSeconds: number;
  ventSeconds: number;
  firstContractionAt?: number | null;
  endedEarly: boolean;
  recoveryBreathsCompleted: number;
  source: ApiTimeSource;
}

export interface ApiMetrics {
  relaxation?: number | null;
  recovery?: number | null;
  technique?: number | null;
  perceivedEffort?: number | null;
}

export interface ApiWarningSigns {
  occurred: boolean;
  kind: ApiWarningKind;
  description?: string | null;
}

export interface ApiWalkRep {
  distanceMeters?: number | null;
  steps?: number | null;
  source: ApiTimeSource;
}

export interface ApneaSessionDto {
  userId: string;
  sortKey: string;
  sessionId: string;
  type: ApiSessionType;
  startedAt: string;
  completedAt?: string | null;
  planVersion?: number | null;
  phase?: number | null;
  plannedSets: ApiPlannedSet[];
  actualSets: ApiActualSet[];
  metrics: ApiMetrics;
  focus?: string | null;
  observations?: string | null;
  takeaway?: string | null;
  nextFocus?: string | null;
  warningSigns: ApiWarningSigns;
  decision?: ApiDecision | null;
  walk?: { reps: ApiWalkRep[]; surface?: string | null } | null;
  bolt?: { seconds: number; source: ApiTimeSource } | null;
  maxTest?: {
    seconds: number;
    contractionsAt?: number | null;
    warningSigns: boolean;
    source: ApiTimeSource;
  } | null;
  aborted: boolean;
  abortReason?: string | null;
}

export type SaveApneaSessionRequest = Omit<ApneaSessionDto, 'userId' | 'sortKey' | 'sessionId'>
  & { sessionId?: string };

export interface ApneaPlanDto {
  userId: string;
  sortKey: string;
  version: number;
  createdAt: string;
  createdBy: string;
  supersedes?: number | null;
  goals: string[];
  currentCertification?: string | null;
  targetCertification?: string | null;
  co2Phase?: number | null;
  o2Phase?: number | null;
  co2BaseComplete: boolean;
  weeklySchedule: Record<string, string>;
  baselines: {
    maxStaticSeconds?: number | null;
    boltSeconds?: number | null;
    measuredAt?: string | null;
    isEstimate: boolean;
  };
  nextRetestDue?: string | null;
  checkInCadenceDays: number;
  rationale?: string | null;
}

export interface ApneaProfileDto {
  userId: string;
  sortKey: string;
  acknowledgements: {
    dryLandOnly: boolean;
    contraindications: boolean;
    acknowledgedAt?: string | null;
  };
  medicalNotes?: string | null;
  frenzelProgress: Record<string, boolean>;
  nauiSkills: Record<string, boolean>;
  preferences: {
    audioCues: boolean;
    voiceAnnouncements: boolean;
    vibration: boolean;
    purgeEnabled: boolean;
  };
  updatedAt?: string | null;
}

export interface ApneaContext {
  recentSessions: ApneaSessionDto[];
  activePlan?: ApneaPlanDto | null;
  profile?: ApneaProfileDto | null;
}

/** Rule-engine session type, which uses lowercase discriminators. */
export type RuleSessionType =
  | 'co2' | 'o2' | 'noContraction' | 'apneaWalk' | 'dryStatic' | 'bolt' | 'breathing';

const RULE_TYPE: Record<ApiSessionType, RuleSessionType> = {
  Co2: 'co2',
  O2: 'o2',
  NoContraction: 'noContraction',
  ApneaWalk: 'apneaWalk',
  DryStatic: 'dryStatic',
  Bolt: 'bolt',
  Breathing: 'breathing',
};

export const toRuleSessionType = (type: ApiSessionType): RuleSessionType => RULE_TYPE[type];

export const toApiSessionType = (type: RuleSessionType): ApiSessionType =>
  (Object.keys(RULE_TYPE) as ApiSessionType[]).find(k => RULE_TYPE[k] === type) ?? 'Breathing';

